import { FC } from 'react';
import {
  Datagrid,
  Filter,
  FunctionField,
  ListProps,
  SearchInput,
  SelectInput,
  TextField,
  TopToolbar,
} from 'react-admin';
import { Theme, useMediaQuery } from '@material-ui/core';
import { dateFormat, dateFormatUTC } from '../../utils/helper';
import EmptyList from '../../component/EmptyList';
import PaginationWithCustomEmpty from '../../component/PaginationWithCustomEmpty';
import CustomBreadcrumb from '../../component/layout/Breadcrumb';
import EnterpriseList from '../../component/ra-enterprise/List';
import DateFilter from './DateFilter';
import SubscriptionsAside from './SubscriptionsAside';

const SubscriptionsFilter = (props: any) => {
  const { isSmall } = props;
  return (
    <Filter {...props}>
      {isSmall && <SearchInput source="searchKey" alwaysOn />}
      {isSmall && (
        <SelectInput
          label="Plan"
          source="planFilter"
          choices={[
            { id: 'monthlyOld', name: 'Monthly Old' },
            { id: 'monthlyNew', name: 'Monthly New' },
            { id: 'monthlyFreeTrial', name: 'Monthly Free Trial' },
            { id: 'yearlyOld', name: 'Yearly Old' },
            { id: 'yearlyNew', name: 'Yearly New' },
            { id: 'yearlyFreeTrial', name: 'Yearly Free Trial' },
            { id: 'lifeTime', name: 'Life Time' },
          ]}
        />
      )}
      {isSmall && <DateFilter alwaysOn />}
    </Filter>
  );
};

const ListActions = (props: any) => {
  const { className } = props;
  return (
    <TopToolbar className={className}>
      <CustomBreadcrumb variant="actions" />
    </TopToolbar>
  );
};

const SubscriptionsColumn = {
  billingDate: (
    <FunctionField
      key="billingDate"
      source="billingDate"
      label="Date"
      render={(record: any) => dateFormat(record.billingDate, 'MM/DD/YY')}
    />
  ),
  billingDateUTC: (
    <FunctionField
      key="billingDateUTC"
      source="billingDate"
      label="Date in UTC"
      render={(record: any) => dateFormatUTC(record.billingDate, 'MM/DD/YY')}
    />
  ),
  firstName: (
    <FunctionField
      key="firstName"
      label="Full Name"
      render={(record: any) =>
        record.firstName || record.lastName
          ? `${record.firstName || ''} ${record.lastName || ''}`.trim()
          : record.email || '--'
      }
    />
  ),
  subscriptionPrice: (
    <FunctionField
      key="subscriptionPrice"
      label="Transaction Amount"
      render={(record: any) => `$${record.subscriptionPrice}`}
    />
  ),
  ProductId: (
    <FunctionField
      key="ProductId"
      label="Subscription Plan"
      render={(record: any) => {
        if (record.ProductId === '1000') return 'Lifetime';
        if (record.ProductId === '999') return 'Monthly';
        if (record.ProductId === '998') return 'Yearly';
        return record.ProductId;
      }}
    />
  ),
  Type: (
    <FunctionField
      key="Type"
      label="Type"
      render={(record: any) => {
        if (record.Type === 'PAID') return 'New';
        if (record.Type === 'RENEWED') return 'Renewal';
        return record.Type;
      }}
    />
  ),
  Device: (
    <FunctionField
      key="Device"
      label="Device"
      render={(record: any) => {
        if (record.Device === 'ios') return 'iOS';
        if (record.Device === 'android') return 'Android';
        return record.Device;
      }}
    />
  ),
};

const ListSubscriptions: FC<ListProps> = (props) => {
  const isXsmall = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('xs')
  );

  const isSmall = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));

  return (
    <EnterpriseList
      {...props}
      preferenceKey="subscription-transactions.list"
      hasColumnsSelector={false}
      bulkActionButtons={false}
      defaultColumns={SubscriptionsColumn}
      hasViewSelector={false}
      filters={isSmall ? <SubscriptionsFilter isSmall={isSmall} /> : undefined}
      aside={<SubscriptionsAside />}
      actions={<ListActions />}
      perPage={25}
      exporter={false}
      hasCreate={false}
      title="Subscriptions"
      pagination={<PaginationWithCustomEmpty />}
      sort={{ field: 'billingDate', order: 'DESC' }}
    >
      {(rest) => {
        const { ids, data, ...restWithoutData } = rest;
        return (
          <Datagrid
            empty={<EmptyList resource={'Subscriptions'} />}
            {...restWithoutData}
          >
            {Object.values(SubscriptionsColumn)}
          </Datagrid>
        );
      }}
    </EnterpriseList>
  );
};

export default ListSubscriptions;
